import React, { Suspense, lazy, useEffect } from 'react';
import { Switch, Route, Router as WouterRouter, useLocation } from "wouter";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import NotFound from "@/pages/not-found";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { Analytics } from "@vercel/analytics/react";

function ScrollToTop() {
  const [location] = useLocation();
  useEffect(() => {
    // Honour in-page anchors (e.g. /digital-khata/delete-account#delete-data); otherwise start at the top.
    const id = window.location.hash ? decodeURIComponent(window.location.hash.slice(1)) : '';
    if (id) {
      let tries = 0;
      const tryScroll = () => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'instant', block: 'start' });
        else if (tries++ < 20) window.setTimeout(tryScroll, 50);
      };
      tryScroll();
      return;
    }
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [location]);
  return null;
}

type PageModule = { default: React.ComponentType<any> };

/**
 * React.lazy wrapper that can be preloaded. Once the module has loaded, the page renders
 * synchronously, so the first client render never flashes the Suspense spinner over the
 * prerendered HTML (which would push LCP back by seconds on mobile).
 */
function lazyPage(load: () => Promise<PageModule>) {
  let Loaded: React.ComponentType<any> | null = null;
  const preload = () => load().then((m) => { Loaded = m.default; return m; });
  const Lazy = lazy(preload);
  function Page(props: any) {
    return Loaded ? <Loaded {...props} /> : <Lazy {...props} />;
  }
  Page.preload = preload;
  return Page;
}

const HomePage = lazyPage(() => import("@/pages/HomePage"));
const ProductsPage = lazyPage(() => import("@/pages/ProductsPage"));
const ProductPage = lazyPage(() => import("@/pages/ProductPage"));
const ServicesHubPage = lazyPage(() => import("@/pages/ServicesHubPage"));
const ServicePage = lazyPage(() => import("@/pages/ServicePage"));
const AboutPage = lazyPage(() => import("@/pages/AboutPage"));
const BlogPage = lazyPage(() => import("@/pages/BlogPage"));
const BlogPostPage = lazyPage(() => import("@/pages/BlogPostPage"));
const ContactPage = lazyPage(() => import("@/pages/ContactPage"));
const PrivacyPage = lazyPage(() => import("@/pages/PrivacyPage"));
const TermsPage = lazyPage(() => import("@/pages/TermsPage"));
const UsCitizenshipTestPrivacyPage = lazyPage(() => import("@/pages/UsCitizenshipTestPrivacyPage"));
const UsCitizenshipTestTermsPage = lazyPage(() => import("@/pages/UsCitizenshipTestTermsPage"));
const CanadianCitizenshipTestPrivacyPage = lazyPage(() => import("@/pages/CanadianCitizenshipTestPrivacyPage"));
const CanadianCitizenshipTestTermsPage = lazyPage(() => import("@/pages/CanadianCitizenshipTestTermsPage"));
const UkCitizenshipTestPrivacyPage = lazyPage(() => import("@/pages/UkCitizenshipTestPrivacyPage"));
const UkCitizenshipTestTermsPage = lazyPage(() => import("@/pages/UkCitizenshipTestTermsPage"));
const SudokuCovePrivacyPage = lazyPage(() => import("@/pages/SudokuCovePrivacyPage"));
const SudokuCoveTermsPage = lazyPage(() => import("@/pages/SudokuCoveTermsPage"));
const AquaBlissPrivacyPage = lazyPage(() => import("@/pages/AquaBlissPrivacyPage"));
const AquaBlissTermsPage = lazyPage(() => import("@/pages/AquaBlissTermsPage"));
const WonderFindPrivacyPage = lazyPage(() => import("@/pages/WonderFindPrivacyPage"));
const WonderFindTermsPage = lazyPage(() => import("@/pages/WonderFindTermsPage"));
const XoArenaPrivacyPage = lazyPage(() => import("@/pages/XoArenaPrivacyPage"));
const XoArenaTermsPage = lazyPage(() => import("@/pages/XoArenaTermsPage"));
const PetalgrovePrivacyPage = lazyPage(() => import("@/pages/PetalgrovePrivacyPage"));
const PetalgroveTermsPage = lazyPage(() => import("@/pages/PetalgroveTermsPage"));
const ArrowEscapePrivacyPage = lazyPage(() => import("@/pages/ArrowEscapePrivacyPage"));
const ArrowEscapeTermsPage = lazyPage(() => import("@/pages/ArrowEscapeTermsPage"));
const PoliceChiefPrivacyPage = lazyPage(() => import("@/pages/PoliceChiefPrivacyPage"));
const PoliceChiefTermsPage = lazyPage(() => import("@/pages/PoliceChiefTermsPage"));
const RepzoPrivacyPage = lazyPage(() => import("@/pages/RepzoPrivacyPage"));
const RepzoTermsPage = lazyPage(() => import("@/pages/RepzoTermsPage"));
const DigitalKhataPrivacyPage = lazyPage(() => import("@/pages/DigitalKhataPrivacyPage"));
const DigitalKhataTermsPage = lazyPage(() => import("@/pages/DigitalKhataTermsPage"));
const DigitalKhataDeleteAccountPage = lazyPage(() => import("@/pages/DigitalKhataDeleteAccountPage"));

// Order matters: first match wins, mirroring <Switch>.
const routes: { path: string; component: ReturnType<typeof lazyPage> }[] = [
  { path: "/", component: HomePage },
  { path: "/products", component: ProductsPage },
  { path: "/products/:slug", component: ProductPage },
  { path: "/services", component: ServicesHubPage },
  { path: "/services/:slug", component: ServicePage },
  { path: "/about", component: AboutPage },
  { path: "/blog", component: BlogPage },
  { path: "/blog/:slug", component: BlogPostPage },
  { path: "/contact", component: ContactPage },
  { path: "/privacy", component: PrivacyPage },
  { path: "/terms", component: TermsPage },
  { path: "/us-citizenship-test/privacy", component: UsCitizenshipTestPrivacyPage },
  { path: "/us-citizenship-test/terms", component: UsCitizenshipTestTermsPage },
  { path: "/canadian-citizenship-test/privacy", component: CanadianCitizenshipTestPrivacyPage },
  { path: "/canadian-citizenship-test/terms", component: CanadianCitizenshipTestTermsPage },
  { path: "/uk-citizenship-test/privacy", component: UkCitizenshipTestPrivacyPage },
  { path: "/uk-citizenship-test/terms", component: UkCitizenshipTestTermsPage },
  { path: "/sudoku-cove/privacy", component: SudokuCovePrivacyPage },
  { path: "/sudoku-cove/terms", component: SudokuCoveTermsPage },
  { path: "/aqua-bliss/privacy", component: AquaBlissPrivacyPage },
  { path: "/aqua-bliss/terms", component: AquaBlissTermsPage },
  { path: "/wonderfind/privacy", component: WonderFindPrivacyPage },
  { path: "/wonderfind/terms", component: WonderFindTermsPage },
  { path: "/xo-arena/privacy", component: XoArenaPrivacyPage },
  { path: "/xo-arena/terms", component: XoArenaTermsPage },
  { path: "/petalgrove/privacy", component: PetalgrovePrivacyPage },
  { path: "/petalgrove/terms", component: PetalgroveTermsPage },
  { path: "/arrow-escape/privacy", component: ArrowEscapePrivacyPage },
  { path: "/arrow-escape/terms", component: ArrowEscapeTermsPage },
  { path: "/police-chief/privacy", component: PoliceChiefPrivacyPage },
  { path: "/police-chief/terms", component: PoliceChiefTermsPage },
  { path: "/repzo/privacy", component: RepzoPrivacyPage },
  { path: "/repzo/terms", component: RepzoTermsPage },
  { path: "/digital-khata/privacy", component: DigitalKhataPrivacyPage },
  { path: "/digital-khata/terms", component: DigitalKhataTermsPage },
  { path: "/digital-khata/delete-account", component: DigitalKhataDeleteAccountPage },
];

function matchesPath(pattern: string, pathname: string) {
  const re = new RegExp(`^${pattern.replace(/:[^/]+/g, "[^/]+")}/?$`);
  return re.test(pathname);
}

/** Loads the page chunk for `pathname` so the first render can show it immediately. */
export function preloadRoute(pathname: string): Promise<unknown> {
  const route = routes.find((r) => matchesPath(r.path, pathname));
  return route ? route.component.preload() : Promise.resolve();
}

const queryClient = new QueryClient();

type AppProps = {
  ssrPath?: string;
  ssrSearch?: string;
};

function PageLoader() {
  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-[#0A0A0A]">
      <div className="w-8 h-8 border-4 border-white border-t-transparent rounded-full animate-spin"></div>
    </div>
  );
}

function Router() {
  return (
    <div className="flex flex-col min-h-screen font-sans bg-[#0A0A0A] text-white">
      <ScrollToTop />
      <Header />
      <main className="grow">
        <Suspense fallback={<PageLoader />}>
          <Switch>
            {routes.map((r) => (
              <Route key={r.path} path={r.path} component={r.component} />
            ))}
            <Route component={NotFound} />
          </Switch>
        </Suspense>
      </main>
      <Footer />
    </div>
  );
}

function App({ ssrPath, ssrSearch }: AppProps) {
  const isBrowser = typeof window !== 'undefined';

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <WouterRouter
          base={import.meta.env.BASE_URL.replace(/\/$/, "")}
          ssrPath={ssrPath}
          ssrSearch={ssrSearch}
        >
          <Router />
        </WouterRouter>
        <Toaster />
      </TooltipProvider>
      {isBrowser ? <Analytics /> : null}
    </QueryClientProvider>
  );
}

export default App;
